import { useState, useEffect, Dispatch } from "react";
import { StopEventPropagation } from "../../../helper/ui/methods";
import { UserInterface } from "../../../middleware/interfaces/user/user-interface";
import { FormatDateBE } from "../../../helper/general/methods";
import { ProjectInterface } from "../../../middleware/interfaces/project/project-interface";
import { UserDecisionInterface } from "../../../middleware/interfaces/user/user-decision-interface";

interface projectDialogProps {
  reveal: string;
  user: UserInterface;
  selectedProject: ProjectInterface | undefined | null;
  setProjectDialogReveal: Dispatch<React.SetStateAction<string>>;
  dispatch: Dispatch<any>;
}

const ProjectDialog: React.FC<projectDialogProps> = ({
  reveal,
  user,
  selectedProject,
  setProjectDialogReveal,
  dispatch,
}) => {
  const [projectData, setProjectData] = useState({
    projectName: "",
    projectLocation: "",
    projectStartDate: "",
    projectEndDate: "",
    projectDescription: "",
  });

  const HandleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setProjectData((prev) => ({ ...prev, [name]: value }));
  };

  // <input type="date"> only accepts strict "YYYY-MM-DD" values
  const ToDateInputValue = (date: string) => {
    if (!date) return "";
    const time = new Date(date).getTime();
    if (isNaN(time) || time === 0) return "";
    const match = date.match(/^\d{4}-\d{2}-\d{2}/);
    if (match) return match[0];
    const parsed = new Date(time);
    const month = String(parsed.getMonth() + 1).padStart(2, "0");
    const day = String(parsed.getDate()).padStart(2, "0");
    return `${parsed.getFullYear()}-${month}-${day}`;
  };

  const CreateProject = () => {
    setProjectDialogReveal("");
    const newProject: ProjectInterface = {
      ...projectData,
      projectUserRole: "OWNER",
      email: user.email,
      projectCreationDate: FormatDateBE(new Date()),
      projectFiles: [],
      projectFolders: [],
      projectReports: [],
      projectForges: [],
      type: "ProjectInterface",
    };
    dispatch({ type: "CREATE_PROJECT", payload: newProject });
  };

  const EditProject = () => {
    setProjectDialogReveal("");
    const decision: UserDecisionInterface = {
      title: "Save changes?",
      message: "The project details are updated for everyone it is shared with.",
      confirmLabel: "Save",
      decisionOptions: ["EDIT_PROJECT", "NO_ACTION"],
      result: "",
      data: {
        email: user.email,
        projectUserRole: selectedProject?.projectUserRole!,
        projectID: selectedProject?.projectID!,
        projectName: projectData.projectName,
        projectLocation: projectData.projectLocation,
        projectDescription: projectData.projectDescription,
        projectStartDate: projectData.projectStartDate,
        projectEndDate: projectData.projectEndDate,
        projectEditDate: FormatDateBE(new Date()),
        type: "ProjectEditInterface",
      },
      type: "UserDecisionInterface",
    };
    dispatch({ type: "DECISION", decision: decision });
  };

  useEffect(() => {
    if (reveal === "") {
      setProjectData({
        projectName: "",
        projectLocation: "",
        projectStartDate: "",
        projectEndDate: "",
        projectDescription: "",
      });
    } else if (reveal === "EDIT") {
      setProjectData({
        projectName: selectedProject?.projectName!,
        projectLocation: selectedProject?.projectLocation ?? "",
        projectStartDate: selectedProject?.projectStartDate ?? "",
        projectEndDate: selectedProject?.projectEndDate ?? "",
        projectDescription: selectedProject?.projectDescription ?? "",
      });
    }
  }, [reveal]);

  if (reveal !== "CREATE" && reveal !== "EDIT") {
    return null;
  }

  const isCreate = reveal === "CREATE";

  return (
    <div className="dialogCover" onClick={StopEventPropagation}>
      <div className="dialog dialogWide">
        <div className="dialogHeader">
          <div className="dialogHeading">
            <h2 className="dialogTitle">
              {isCreate ? "Create project" : "Edit project"}
            </h2>
          </div>
          <span
            className="dialogClose"
            onClick={() => setProjectDialogReveal("")}
          >
            ×
          </span>
        </div>
        <div className="dialogBody dialogFieldGrid">
          <div className="dialogValueWrapper">
            <span className="dialogInputLabel">Name</span>
            <input
              type="text"
              name="projectName"
              placeholder="Project name"
              value={projectData.projectName}
              onChange={HandleInputChange}
            />
          </div>
          <div className="dialogValueWrapper">
            <span className="dialogInputLabel">Location</span>
            <input
              type="text"
              name="projectLocation"
              placeholder="Project location"
              value={projectData.projectLocation}
              onChange={HandleInputChange}
            />
          </div>
          <div className="dialogValueWrapper">
            <span className="dialogInputLabel">Start date</span>
            <input
              type="date"
              name="projectStartDate"
              value={
                isCreate
                  ? projectData.projectStartDate
                  : ToDateInputValue(projectData.projectStartDate)
              }
              onChange={HandleInputChange}
            />
          </div>
          <div className="dialogValueWrapper">
            <span className="dialogInputLabel">End date</span>
            <input
              type="date"
              name="projectEndDate"
              value={
                isCreate
                  ? projectData.projectEndDate
                  : ToDateInputValue(projectData.projectEndDate)
              }
              onChange={HandleInputChange}
            />
          </div>
          <div className="dialogTextInputWrapper">
            <span className="dialogInputLabel">Description</span>
            <textarea
              name="projectDescription"
              placeholder="Optional"
              value={projectData.projectDescription}
              onChange={HandleInputChange}
            />
          </div>
        </div>
        <div className="dialogButtonWrapper">
          <input
            type="button"
            name="cancel"
            value="Cancel"
            onClick={() => setProjectDialogReveal("")}
          />
          <input
            className="dialogPrimaryButton"
            type="button"
            name={isCreate ? "create" : "save"}
            value={isCreate ? "Create" : "Save"}
            onClick={isCreate ? CreateProject : EditProject}
          />
        </div>
      </div>
    </div>
  );
};

export default ProjectDialog;
